import { BaseApi } from './_base.api'
import {
  ISubscriptionStatus,
  IPlan,
  ISubscribeRequest,
  ISubscribeResponse,
  ICancelSubscriptionResponse,
  IInvoice,
  IPortalUrlResponse,
} from '@typings'

export class BillingApi extends BaseApi {
  /**
   * Get current subscription status
   */
  getStatus = () =>
    this.api.get<any>('/billing/status').then(res => {
      const data = res.data

      // Transform nested backend response structure
      return {
        status: data.status,
        tier: data.tier,
        trial_ends_at: data.trial?.ends_at || null,
        ends_at: data.subscription?.ends_at || null,
        is_on_trial: data.trial?.active || false,
        is_on_grace_period: data.subscription?.on_grace_period || false,
        trial_days_remaining: data.trial?.days_remaining || null,
        can_edit_documents: data.can_edit_documents,
        can_finalize_documents: data.can_finalize_documents,
        features: data.features,
        limits: data.limits || null,
        usage: data.usage || null,
        email_verified: data.email_verified,
        billing_interval: data.billing_interval || null,
        started_at: data.started_at || null,
        current_period_end: data.current_period_end || null,
        returning_to_free_tier: data.subscription?.returning_to_free_tier || false,
        free_tier_date: data.subscription?.free_tier_date || null,
      } as ISubscriptionStatus
    })

  /**
   * Get available plans
   */
  getPlans = () =>
    this.api.get<{ plans: any[] }>('/billing/plans').then(res => {
      const backendPlans = res.data.plans || []
      const proPlan = backendPlans.find((p: any) => p.slug === 'pro')

      if (!proPlan) return []

      // Transform single PRO plan into two separate plan objects (monthly and yearly)
      return [
        {
          id: 'pro_monthly',
          name: proPlan.name || 'PRO',
          price: proPlan.price_monthly / 100,
          interval: 'month' as const,
          stripe_price_id: proPlan.price_id_monthly,
          trial_days: proPlan.trial_days || 30,
          features: proPlan.features || [],
        },
        {
          id: 'pro_yearly',
          name: proPlan.name || 'PRO',
          price: proPlan.price_yearly / 100,
          interval: 'year' as const,
          stripe_price_id: proPlan.price_id_yearly,
          trial_days: proPlan.trial_days || 30,
          features: proPlan.features || [],
          savings: 'Bespaar €8,88',
        },
      ] as IPlan[]
    })

  /**
   * Subscribe to a plan (start trial or upgrade)
   */
  subscribe = (data: ISubscribeRequest) =>
    this.api.post<ISubscribeResponse>('/billing/checkout', data).then(res => res.data)

  /**
   * Cancel subscription
   */
  cancelSubscription = () => this.api.post<ICancelSubscriptionResponse>('/billing/cancel').then(res => res.data)

  /**
   * Resume cancelled subscription
   */
  resumeSubscription = () =>
    this.api.post<{ success: boolean; message: string }>('/billing/resume').then(res => res.data)

  /**
   * Get invoices
   */
  getInvoices = () => this.api.get<{ invoices: IInvoice[] }>('/billing/invoices').then(res => res.data.invoices)

  /**
   * Get Stripe Customer Portal URL
   */
  getPortalUrl = () =>
    this.api.get<{ portal_url: string }>('/billing/portal').then(res => ({
      url: res.data.portal_url,
    }))
}
